import { translate } from '../../i18n/index.js';
import type { MembershipPlan } from '@atomfit/shared';
import { ArrowRight, Check } from 'lucide-react';
import { formatCurrency } from '../design/AtomFitUI.js';
import { membershipHourlyMarketingPrice } from '../../utils/membership-pricing.js';

interface MembershipPlanCardsProps {
  plans: MembershipPlan[];
  onChoose: (plan: MembershipPlan) => void;
  buttonTabIndex?: number;
  showIncludedAmenities?: boolean;
}

export const membershipSelectionCopy = {
  title: translate(
    'components.membership.membership_plan_cards.membership_selection_copy.membership',
  ),
  description: translate(
    'components.membership.membership_plan_cards.membership_selection_copy.training',
  ),
} as const;

export function MembershipPlanCards({
  plans,
  onChoose,
  buttonTabIndex,
  showIncludedAmenities = false,
}: MembershipPlanCardsProps) {
  return (
    <div
      className={`af-membership-price-cards af-membership-price-cards--select${plans.length >= 3 ? ' has-three' : ''}`}
    >
      {plans.map((plan) => {
        const isElite = plan.tier === 'ELITE';
        const hourlyPriceInSmallestUnit = membershipHourlyMarketingPrice(plan);

        return (
          <article
            className={`af-price-card af-price-card--membership af-price-card--${isElite ? 'gold' : 'teal'}`}
            key={plan.id}
          >
            <h4>{plan.name}</h4>
            <strong className="af-price-card-price">
              {formatCurrency(hourlyPriceInSmallestUnit, plan.currency)}
            </strong>
            <span className="af-price-card-period">
              {translate(
                'components.membership.membership_plan_cards.membership_plan_cards.span_price_card_period.copy',
              )}
            </span>
            <span className="af-price-card-monthly">
              {translate(
                'components.membership.membership_plan_cards.membership_plan_cards.span_price_card_monthly.payment',
              )}
              {formatCurrency(plan.priceInSmallestUnit, plan.currency)}
            </span>
            <ul className="af-price-features">
              {membershipFeatures(plan, showIncludedAmenities).map((feature) => (
                <li key={feature}>
                  <Check aria-hidden="true" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <button
              className="af-price-card-button"
              type="button"
              tabIndex={buttonTabIndex}
              onClick={() => onChoose(plan)}
            >
              {translate(
                'components.membership.membership_plan_cards.membership_plan_cards.button_price_card_button.label',
              )}{' '}
              {plan.name} <ArrowRight aria-hidden="true" />
            </button>
          </article>
        );
      })}
    </div>
  );
}

function membershipFeatures(plan: MembershipPlan, showIncludedAmenities: boolean) {
  const features: string[] = [];

  if (plan.slotsPerMonth) {
    const trainingHours = plan.slotsPerMonth / 2;
    features.push(
      translate('components.membership.membership_plan_cards.membership_features.push.training', {
        value1: formatHours(trainingHours),
      }),
    );
  }

  features.push(
    translate('components.membership.membership_plan_cards.membership_features.push.copy'),
    translate('components.membership.membership_plan_cards.membership_features.push.copy_2'),
  );

  const advanceMonths = Math.max(1, Math.round(plan.bookingAdvanceDays / 31));
  features.push(
    translate('components.membership.membership_plan_cards.membership_features.push.booking', {
      advanceMonths: advanceMonths,
      value2:
        advanceMonths === 1
          ? translate('components.membership.membership_plan_cards.membership_features.value2')
          : translate('components.membership.membership_plan_cards.membership_features.value2_2'),
    }),
  );

  if (plan.guestPassesPerMonth > 0) {
    features.push(
      translate('components.membership.membership_plan_cards.membership_features.push.time_guest', {
        value1: plan.guestPassesPerMonth,
      }),
    );
  }

  features.push(
    translate('components.membership.membership_plan_cards.membership_features.push.copy_3'),
  );
  if (showIncludedAmenities) {
    features.push(
      translate('components.membership.membership_plan_cards.membership_features.push.amenities'),
    );
  }
  return features;
}

function formatHours(hours: number) {
  const formatted = Number.isInteger(hours) ? String(hours) : String(hours).replace('.', ',');
  const unit =
    hours === 1
      ? translate('components.membership.membership_plan_cards.format_hours.unit')
      : hours >= 2 && hours <= 4
        ? translate('components.membership.membership_plan_cards.format_hours.unit_2')
        : translate('components.membership.membership_plan_cards.format_hours.unit_3');
  return translate('components.membership.membership_plan_cards.format_hours.copy', {
    hours: formatted,
    unit,
  });
}
